import { useState, useRef } from 'react';
import { useSeoMeta } from '@unhead/react';
import { Layout } from '@/components/Layout';
import { useAppContext } from '@/hooks/useAppContext';
import { APP_RELAYS } from '@/lib/appRelays';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

/* ── Icons ──────────────────────────────────────────────────────────────── */
function TrashIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
    </svg>
  );
}

function RelayReadIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" title="Read">
      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
    </svg>
  );
}

function RelayWriteIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" title="Write">
      <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
    </svg>
  );
}

/* ── Section wrapper ─────────────────────────────────────────────────────── */
function Section({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <section
      className="rounded-xl overflow-hidden"
      style={{ border: '1px solid var(--gb-border)', background: 'var(--bg-secondary)' }}
    >
      <div className="px-5 py-4" style={{ borderBottom: '1px solid var(--gb-border)' }}>
        <h2 className="font-semibold text-base" style={{ color: 'var(--text-primary)' }}>{title}</h2>
        {description && (
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>{description}</p>
        )}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

/* ── Relay row ───────────────────────────────────────────────────────────── */
interface RelayRowProps {
  url: string;
  read: boolean;
  write: boolean;
  isDefault?: boolean;
  onToggleRead?: () => void;
  onToggleWrite?: () => void;
  onRemove?: () => void;
}

function RelayRow({ url, read, write, isDefault, onToggleRead, onToggleWrite, onRemove }: RelayRowProps) {
  return (
    <div
      className="flex items-center gap-3 py-2.5 px-3 rounded-lg"
      style={{ background: 'var(--bg-primary)' }}
    >
      {/* Status dot */}
      <span
        className="w-2 h-2 rounded-full shrink-0"
        style={{ background: read || write ? 'var(--gb-accent)' : 'var(--gb-border)' }}
      />

      {/* URL */}
      <span
        className="flex-1 text-sm font-mono truncate min-w-0"
        style={{ color: 'var(--text-primary)' }}
        title={url}
      >
        {url}
      </span>

      {isDefault && (
        <Badge
          variant="secondary"
          className="text-xs shrink-0 hidden sm:inline-flex"
          style={{ background: 'rgba(100,100,100,0.15)', color: 'var(--text-secondary)' }}
        >
          default
        </Badge>
      )}

      {/* Read toggle */}
      <button
        onClick={onToggleRead}
        title={read ? 'Read enabled — click to disable' : 'Read disabled — click to enable'}
        className={cn(
          'flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors shrink-0',
        )}
        style={{
          background: read ? 'rgba(29,161,242,0.15)' : 'rgba(100,100,100,0.1)',
          color: read ? '#1da1f2' : 'var(--text-secondary)',
          border: `1px solid ${read ? 'rgba(29,161,242,0.3)' : 'var(--gb-border)'}`,
        }}
      >
        <RelayReadIcon />
        <span className="hidden sm:inline">Read</span>
      </button>

      {/* Write toggle */}
      <button
        onClick={onToggleWrite}
        title={write ? 'Write enabled — click to disable' : 'Write disabled — click to enable'}
        className={cn(
          'flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors shrink-0',
        )}
        style={{
          background: write ? 'rgba(29,161,242,0.15)' : 'rgba(100,100,100,0.1)',
          color: write ? '#1da1f2' : 'var(--text-secondary)',
          border: `1px solid ${write ? 'rgba(29,161,242,0.3)' : 'var(--gb-border)'}`,
        }}
      >
        <RelayWriteIcon />
        <span className="hidden sm:inline">Write</span>
      </button>

      {/* Remove (custom only) */}
      {onRemove ? (
        <button
          onClick={onRemove}
          title="Remove relay"
          className="flex items-center justify-center w-7 h-7 rounded transition-colors shrink-0"
          style={{ color: '#ef4444' }}
          onMouseEnter={e => (e.currentTarget.style.background = 'rgba(239,68,68,0.1)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
        >
          <TrashIcon />
        </button>
      ) : (
        <div className="w-7 shrink-0" />
      )}
    </div>
  );
}

/* ── Main page ───────────────────────────────────────────────────────────── */
export default function SettingsPage() {
  const { config, updateConfig } = useAppContext();

  useSeoMeta({ title: 'Settings – Goblinbox' });

  // ── Relay state ──
  const [newRelayUrl, setNewRelayUrl] = useState('');
  const [newRelayRead, setNewRelayRead] = useState(true);
  const [newRelayWrite, setNewRelayWrite] = useState(true);
  const [relayError, setRelayError] = useState('');
  const relayInputRef = useRef<HTMLInputElement>(null);

  // ── Hashtag state ──
  const [newTag, setNewTag] = useState('');
  const [tagError, setTagError] = useState('');
  const hashtagFilters: string[] = config.hashtagFilters ?? [];

  const defaultRelayUrls = new Set(APP_RELAYS.relays.map(r => r.url));
  const currentRelays = config.relayMetadata.relays;

  /* ── Relay helpers ── */
  function updateRelays(updated: typeof currentRelays) {
    updateConfig(c => ({
      ...c,
      relayMetadata: {
        relays: updated,
        updatedAt: Math.floor(Date.now() / 1000),
      },
    }));
  }

  function toggleRelayRead(url: string) {
    updateRelays(currentRelays.map(r => r.url === url ? { ...r, read: !r.read } : r));
  }

  function toggleRelayWrite(url: string) {
    updateRelays(currentRelays.map(r => r.url === url ? { ...r, write: !r.write } : r));
  }

  function removeRelay(url: string) {
    updateRelays(currentRelays.filter(r => r.url !== url));
  }

  function addRelay() {
    setRelayError('');
    const url = newRelayUrl.trim();
    if (!url) return;
    if (!url.startsWith('wss://') && !url.startsWith('ws://')) {
      setRelayError('Relay URL must start with wss:// or ws://');
      return;
    }
    if (currentRelays.some(r => r.url === url)) {
      setRelayError('This relay is already in your list.');
      return;
    }
    updateRelays([...currentRelays, { url, read: newRelayRead, write: newRelayWrite }]);
    setNewRelayUrl('');
    setNewRelayRead(true);
    setNewRelayWrite(true);
    relayInputRef.current?.focus();
  }

  /* ── Hashtag helpers ── */
  function addHashtag() {
    setTagError('');
    const raw = newTag.trim().replace(/^#+/, '').toLowerCase();
    if (!raw) return;
    if (!/^[a-z0-9_-]+$/i.test(raw)) {
      setTagError('Hashtags can only contain letters, numbers, hyphens, and underscores.');
      return;
    }
    if (hashtagFilters.includes(raw)) {
      setTagError('That hashtag is already in your filter list.');
      return;
    }
    updateConfig(c => ({ ...c, hashtagFilters: [...(c.hashtagFilters ?? []), raw] }));
    setNewTag('');
  }

  function removeHashtag(tag: string) {
    updateConfig(c => ({ ...c, hashtagFilters: (c.hashtagFilters ?? []).filter(t => t !== tag) }));
  }

  function clearAllHashtags() {
    updateConfig(c => ({ ...c, hashtagFilters: [] }));
  }

  /* ── Render ── */
  return (
    <Layout>
      <div className="px-4 sm:px-8 py-8" style={{ maxWidth: 820, margin: '0 auto' }}>

        {/* Page header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Settings</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
            Manage your relays and content filters.
          </p>
        </div>

        <div className="flex flex-col gap-6">

          {/* ── Relays ──────────────────────────────────────────────────── */}
          <Section
            title="Relays"
            description="Configure which Nostr relays to read from and publish to. Changes apply immediately."
          >
            {/* Existing relays */}
            <div className="flex flex-col gap-2 mb-5">
              {currentRelays.length === 0 && (
                <p className="text-sm py-4 text-center" style={{ color: 'var(--text-secondary)' }}>
                  No relays configured. Add one below.
                </p>
              )}
              {currentRelays.map(relay => (
                <RelayRow
                  key={relay.url}
                  url={relay.url}
                  read={relay.read}
                  write={relay.write}
                  isDefault={defaultRelayUrls.has(relay.url)}
                  onToggleRead={() => toggleRelayRead(relay.url)}
                  onToggleWrite={() => toggleRelayWrite(relay.url)}
                  onRemove={defaultRelayUrls.has(relay.url) ? undefined : () => removeRelay(relay.url)}
                />
              ))}
            </div>

            {/* Add relay */}
            <div
              className="rounded-lg p-4"
              style={{ background: 'var(--bg-primary)', border: '1px solid var(--gb-border)' }}
            >
              <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-secondary)' }}>
                Add custom relay
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  ref={relayInputRef}
                  value={newRelayUrl}
                  onChange={e => { setNewRelayUrl(e.target.value); setRelayError(''); }}
                  onKeyDown={e => e.key === 'Enter' && addRelay()}
                  placeholder="wss://relay.example.com"
                  className="flex-1 font-mono text-sm"
                  style={{
                    background: 'var(--bg-secondary)',
                    borderColor: relayError ? '#ef4444' : 'var(--gb-border)',
                    color: 'var(--text-primary)',
                  }}
                />
                <div className="flex items-center gap-3 shrink-0">
                  <div className="flex items-center gap-1.5">
                    <Switch
                      id="new-relay-read"
                      checked={newRelayRead}
                      onCheckedChange={setNewRelayRead}
                    />
                    <Label htmlFor="new-relay-read" className="text-xs cursor-pointer" style={{ color: 'var(--text-secondary)' }}>
                      Read
                    </Label>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Switch
                      id="new-relay-write"
                      checked={newRelayWrite}
                      onCheckedChange={setNewRelayWrite}
                    />
                    <Label htmlFor="new-relay-write" className="text-xs cursor-pointer" style={{ color: 'var(--text-secondary)' }}>
                      Write
                    </Label>
                  </div>
                  <button
                    onClick={addRelay}
                    className="flex items-center gap-1.5 px-3 py-2 rounded text-sm font-medium text-white transition-all hover:opacity-90 shrink-0"
                    style={{ background: 'var(--gb-accent)' }}
                  >
                    <PlusIcon />
                    <span className="hidden sm:inline">Add</span>
                  </button>
                </div>
              </div>
              {relayError && (
                <p className="text-xs mt-2" style={{ color: '#ef4444' }}>{relayError}</p>
              )}
            </div>

            {/* Reset to defaults */}
            {currentRelays.some(r => !defaultRelayUrls.has(r.url)) && (
              <div className="mt-3 flex justify-end">
                <button
                  onClick={() => updateRelays(APP_RELAYS.relays)}
                  className="text-xs px-3 py-1.5 rounded transition-colors"
                  style={{ color: 'var(--text-secondary)', border: '1px solid var(--gb-border)' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.05)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  Reset to defaults
                </button>
              </div>
            )}
          </Section>

          {/* ── Hashtag Filters ─────────────────────────────────────────── */}
          <Section
            title="Content Filters"
            description="Only show content tagged with these hashtags in your feeds. Leave empty to see all content."
          >
            {/* Active filters */}
            <div className="mb-5">
              {hashtagFilters.length === 0 ? (
                <div
                  className="rounded-lg py-8 px-4 text-center"
                  style={{ border: '2px dashed var(--gb-border)' }}
                >
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    No hashtag filters active — showing all content.
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  <div className="flex flex-wrap gap-2">
                    {hashtagFilters.map(tag => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium"
                        style={{
                          background: 'rgba(29,161,242,0.12)',
                          border: '1px solid rgba(29,161,242,0.3)',
                          color: '#1da1f2',
                        }}
                      >
                        <span>#</span>
                        <span>{tag}</span>
                        <button
                          onClick={() => removeHashtag(tag)}
                          className="flex items-center justify-center w-4 h-4 rounded-full transition-colors ml-0.5"
                          style={{ color: '#1da1f2' }}
                          onMouseEnter={e => (e.currentTarget.style.background = 'rgba(29,161,242,0.2)')}
                          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                          aria-label={`Remove #${tag}`}
                        >
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                          </svg>
                        </button>
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                      {hashtagFilters.length} active filter{hashtagFilters.length !== 1 ? 's' : ''}
                    </span>
                    <span style={{ color: 'var(--gb-border)' }}>·</span>
                    <button
                      onClick={clearAllHashtags}
                      className="text-xs transition-colors"
                      style={{ color: '#ef4444' }}
                      onMouseEnter={e => (e.currentTarget.style.opacity = '0.75')}
                      onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
                    >
                      Clear all
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Add hashtag */}
            <div
              className="rounded-lg p-4"
              style={{ background: 'var(--bg-primary)', border: '1px solid var(--gb-border)' }}
            >
              <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-secondary)' }}>
                Add hashtag filter
              </p>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <span
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium pointer-events-none select-none"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    #
                  </span>
                  <Input
                    value={newTag}
                    onChange={e => { setNewTag(e.target.value); setTagError(''); }}
                    onKeyDown={e => e.key === 'Enter' && addHashtag()}
                    placeholder="bitcoin, nostr, gaming…"
                    className="pl-7 text-sm"
                    style={{
                      background: 'var(--bg-secondary)',
                      borderColor: tagError ? '#ef4444' : 'var(--gb-border)',
                      color: 'var(--text-primary)',
                    }}
                  />
                </div>
                <button
                  onClick={addHashtag}
                  className="flex items-center gap-1.5 px-4 py-2 rounded text-sm font-medium text-white transition-all hover:opacity-90 shrink-0"
                  style={{ background: 'var(--gb-accent)' }}
                >
                  <PlusIcon />
                  Add
                </button>
              </div>
              {tagError && (
                <p className="text-xs mt-2" style={{ color: '#ef4444' }}>{tagError}</p>
              )}
              <p className="text-xs mt-2" style={{ color: 'var(--text-secondary)' }}>
                Tip: Enter tags one at a time, or press Enter to add quickly.
              </p>
            </div>
          </Section>

          {/* ── Saved indicator ─────────────────────────────────────────── */}
          <div className="flex items-center justify-center gap-1.5 text-xs pb-4" style={{ color: 'var(--text-secondary)' }}>
            <CheckIcon />
            Changes are saved automatically.
          </div>

        </div>
      </div>
    </Layout>
  );
}
